import { PRODUCT_OPTIONS_KEY } from '@yme/selectors/constants';
import { CONTROL_METHOD_KEY, FINAL_PRICE_KEY, LENGTH_KEY, LOWERED_VERSION_METHOD_KEY, POWER_METHOD_KEY, PRICE_KEY, ROTATION_KEY, SHAFT_LENGTH_KEY, SIZE_KEY, STARTING_METHOD_KEY, TRANSMISSION_METHOD_KEY, } from '@yme/selectors/index';
import { formatPrice } from '@yme/utils/formatPrice';
import { getUpperCasedValueForProductType } from '@yme/utils/product';
import { checkBoatsProductType, checkEBikesProductType, checkMarineEngineProductType, checkMotorcyclesProductType, checkScooterProductType, } from '@yme/utils/productTypes';
import { BOATS_TABLE_COLUMNS_CONFIG, DEFAULT_TABLE_COLUMNS_CONFIG, MARINE_ENGINES_TABLE_COLUMNS_CONFIG, MOTORCYCLES_TABLE_COLUMNS_CONFIG, SCOOTERS_TABLE_COLUMNS_CONFIG, TABLE_COLUMNS_EBIKES_CONFIG, THEAD_COLUMNS_BOATS_CONFIG, THEAD_COLUMNS_EBIKES_CONFIG, THEAD_COLUMNS_MARINE_ENGINE_CONFIG, THEAD_COLUMNS_MOTORCYCLES_CONFIG, THEAD_COLUMNS_SCOOTERS_CONFIG, } from './constants';
export function isProductPricingInfoEmpty() {
    return false;
}
export function getPriceValue(item, language) {
    const price = item.prices?.discount ? item.price : ''; // strikethrough price or empty value
    const finalPrice = item.prices?.discount
        ? formatPrice({
            locale: language,
            currencyName: item.prices?.currencyCode,
            price: item.prices.discount.discountedPrice,
        })
        : item.price;
    return {
        price,
        finalPrice,
    };
}
export function determineResponseBasedOnProductType({ productType, onMotorcycles, onMarineEngines, onEBikes, onBoats, onScooters, onDefault, isGroupingProducts, }) {
    if (checkEBikesProductType(productType)) {
        return onEBikes;
    }
    if (checkBoatsProductType(productType) && isGroupingProducts) {
        return onBoats;
    }
    if (checkScooterProductType(productType) && isGroupingProducts) {
        return onScooters;
    }
    if (checkMotorcyclesProductType(productType) && isGroupingProducts) {
        return onMotorcycles;
    }
    if (checkMarineEngineProductType(productType)) {
        return onMarineEngines;
    }
    return onDefault;
}
export function getHeaderLabelByLang(cfg, labels) {
    const { productSize, rotation, shaftLength, startingMethod, controlMethod, powerVersion, transmission, loweredVersion, length, productOption, price, } = labels;
    const resultWithValue = {
        [SIZE_KEY]: productSize,
        [ROTATION_KEY]: rotation,
        [SHAFT_LENGTH_KEY]: shaftLength,
        [STARTING_METHOD_KEY]: startingMethod,
        [CONTROL_METHOD_KEY]: controlMethod,
        [POWER_METHOD_KEY]: powerVersion,
        [TRANSMISSION_METHOD_KEY]: transmission,
        [LOWERED_VERSION_METHOD_KEY]: loweredVersion,
        [LENGTH_KEY]: length,
        [PRODUCT_OPTIONS_KEY]: productOption,
        [PRICE_KEY]: '',
        [FINAL_PRICE_KEY]: price,
    };
    const result = cfg.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const newPropsItem = item?.id
            ? {
                ...item,
                cellValue: resultWithValue[item.id],
            }
            : {
                ...item,
            };
        if (!acc && newPropsItem) {
            return [newPropsItem];
        }
        return [...acc, newPropsItem];
    }, []);
    return result;
}
export function getTableColumnsConfig(isGroupingProducts, productType) {
    return determineResponseBasedOnProductType({
        productType,
        onMotorcycles: MOTORCYCLES_TABLE_COLUMNS_CONFIG,
        onMarineEngines: MARINE_ENGINES_TABLE_COLUMNS_CONFIG,
        onEBikes: TABLE_COLUMNS_EBIKES_CONFIG,
        onBoats: BOATS_TABLE_COLUMNS_CONFIG,
        onScooters: SCOOTERS_TABLE_COLUMNS_CONFIG,
        onDefault: DEFAULT_TABLE_COLUMNS_CONFIG,
        isGroupingProducts,
    });
}
export function getSizeFullText({ size, labels, }) {
    const option = size.toLowerCase();
    const { frameSizeSmall, frameSizeMedium, frameSizeLarge } = labels;
    switch (option) {
        case 's':
            return frameSizeSmall;
        case 'm':
            return frameSizeMedium;
        case 'l':
            return frameSizeLarge;
        default:
            return size;
    }
}
export function getEBikesProps({ data, labels, language, }) {
    const eBikesResult = data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const sizeValue = getSizeFullText({
            size: item.size,
            labels,
        });
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            size: sizeValue,
            pricingDisclaimer: item.pricingDisclaimer,
            productName: item.productName,
            price,
            finalPrice,
        };
        if (!acc) {
            return [itemProp];
        }
        return [...acc, itemProp];
    }, []);
    return eBikesResult;
}
export function getBoatsProps(data, language) {
    const boatsResult = data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            productName: item.productName,
            length: item.productOption,
            pricingDisclaimer: item.pricingDisclaimer,
            price,
            finalPrice,
        };
        if (!acc) {
            return itemProp ? [itemProp] : [];
        }
        return [...acc, itemProp];
    }, []);
    return boatsResult;
}
export function getScootersProps(data, language) {
    const scootersResult = data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            productName: item.productName,
            productOptions: item.productOption,
            pricingDisclaimer: item.pricingDisclaimer,
            price,
            finalPrice,
        };
        if (!acc) {
            return itemProp ? [itemProp] : [];
        }
        return [...acc, itemProp];
    }, []);
    return scootersResult;
}
export function getMarineEngineProps(data, language) {
    const marineEngineResult = data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            productName: item.productName,
            rotation: item.rotation,
            shaftLength: item.shaftLength,
            controlMethod: item.controlMethod,
            startingMethod: item.startingMethod,
            pricingDisclaimer: item.pricingDisclaimer,
            price,
            finalPrice,
        };
        if (!acc) {
            return itemProp ? [itemProp] : [];
        }
        return [...acc, itemProp];
    }, []);
    return marineEngineResult;
}
export function getMotorcyclesProps(data, language) {
    const motorcyclesResult = data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            productName: item.productName,
            power: item.power,
            transmission: item.transmission,
            loweredVersion: item.loweredVersion,
            pricingDisclaimer: item.pricingDisclaimer,
            price,
            finalPrice,
        };
        if (!acc) {
            return itemProp ? [itemProp] : [];
        }
        return [...acc, itemProp];
    }, []);
    return motorcyclesResult;
}
export function getDefaultVariantOfProductByProductType(data, product, language) {
    return data.reduce((acc, item) => {
        if (!item) {
            return [];
        }
        const productNameExtended = item.colourName
            ? `${product.productNameEn} ${item.colourName}`
            : product.productNameEn;
        const { price, finalPrice } = getPriceValue(item, language);
        const itemProp = {
            pricingDisclaimer: item.pricingDisclaimer,
            productNameExtended,
            price,
            finalPrice,
        };
        if (!acc) {
            return [itemProp];
        }
        return [...acc, itemProp];
    }, []);
}
export function getVariantsOfProductByProductType({ data, productType, labels, language, product, isGroupingProducts, }) {
    return determineResponseBasedOnProductType({
        productType,
        onMotorcycles: getMotorcyclesProps(data, language),
        onScooters: getScootersProps(data, language),
        onMarineEngines: getMarineEngineProps(data, language),
        onBoats: getBoatsProps(data, language),
        onEBikes: getEBikesProps({
            data,
            labels,
            language,
        }),
        onDefault: getDefaultVariantOfProductByProductType(data, product, language),
        isGroupingProducts,
    });
}
export function getTHeadColumnsConfig({ productType, labels, isGroupingProducts, }) {
    return determineResponseBasedOnProductType({
        productType,
        onMotorcycles: getHeaderLabelByLang(THEAD_COLUMNS_MOTORCYCLES_CONFIG, labels),
        onMarineEngines: getHeaderLabelByLang(THEAD_COLUMNS_MARINE_ENGINE_CONFIG, labels),
        onScooters: getHeaderLabelByLang(THEAD_COLUMNS_SCOOTERS_CONFIG, labels),
        onEBikes: getHeaderLabelByLang(THEAD_COLUMNS_EBIKES_CONFIG, labels),
        onBoats: getHeaderLabelByLang(THEAD_COLUMNS_BOATS_CONFIG, labels),
        onDefault: undefined,
        isGroupingProducts,
    });
}
export function getTableConfig({ variants = [], isGroupingProducts, productType = '', labels, product, language, }) {
    const productTypeFormatted = getUpperCasedValueForProductType(productType);
    const isProductTypeMarine = checkMarineEngineProductType(productType);
    const isProductTypeEBikes = checkEBikesProductType(productType);
    const isProductTypeMotorcycles = checkMotorcyclesProductType(productType) && isGroupingProducts;
    const isProductTypeScooters = checkScooterProductType(productType) && isGroupingProducts;
    const isProductTypeBoats = checkBoatsProductType(productType) && isGroupingProducts;
    const tHeadColumns = getTHeadColumnsConfig({
        productType: productTypeFormatted,
        labels,
        isGroupingProducts,
    });
    const tableColumnsConfig = getTableColumnsConfig(isGroupingProducts, productTypeFormatted);
    const rows = getVariantsOfProductByProductType({
        data: variants,
        productType,
        labels,
        language,
        product,
        isGroupingProducts,
    });
    const isRowVisible = rows.every((item) => Boolean(item.pricingDisclaimer));
    return {
        isProductTypeOther: !isProductTypeMarine &&
            !isProductTypeEBikes &&
            !isProductTypeMotorcycles &&
            !isProductTypeScooters &&
            !isProductTypeBoats,
        isProductTypeMarine,
        isProductTypeEBikes,
        isProductTypeBoats,
        isProductTypeScooters,
        isProductTypeMotorcycles,
        tHeadColumns,
        rows,
        tableColumnsConfig,
        isRowVisible,
    };
}
